/*
 *
 * SupportPanel
 *
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { RootState } from './types';
import wheelsWhite from 'assets/images/support/wheelsWhite.png';
import wheelsBlack from 'assets/images/support/wheelsBlack.png';
import noFeets from 'assets/images/support/no_feets.png';
import feets10 from 'assets/images/support/feets_10.png';
import hang from 'assets/images/support/hang.png';
import hangByCrossRails from 'assets/images/support/by_crossrails.png';
import feetsA10 from 'assets/images/support/feets_adjustable_10.png';
import BigOptionButton from 'components/BigOptionButton';
import SectionOptions from 'components/SectionOptions';
import {
  restoreCabinet,
  setSupportRequestAction,
} from 'containers/Configurator/actions/furniture';
import {
  CabinetSupport,
  Material,
  PositionAngle,
} from 'lib/modelBuilder/constants';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { CustomPiece } from 'lib/modelBuilder/pieces/CustomPiece';
import { Batten } from 'lib/modelBuilder/pieces/Batten';
import styled from 'styled-components/macro';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import { useTranslation } from 'react-i18next';
import { displaySupportPanelAction } from 'containers/SupportPanel/actions';
import { makeSelectContext } from '../Configurator/selectors/general';
import { FurnitureEditorContext } from '../../lib/constraintsChecker/FurnitureEditor';
import { isProduction } from '../../utils/constants';
import FeetsDimensionsForm from './FeetsDimensionsForm';
import useMedia from 'use-media';
import { makeSelectSupportFootCabinet } from '../Configurator/selectors/furniture';

interface StateProps {
  cabinet: Cabinet;
  context: FurnitureEditorContext;
  foot: CustomPiece<Batten> | null;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  cabinet: makeSelectLastHistoryCabinet(),
  context: makeSelectContext(),
  foot: makeSelectSupportFootCabinet(),
});

const SupportOptions = () => {
  const dispatch = useDispatch();
  const { cabinet, context, foot } = useSelector(stateSelector);
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });
  const [displayCustom, setDisplayCustom] = useState(!!foot?.custom);

  let support = CabinetSupport.NoSupport;
  let feet: CustomPiece<Batten> | undefined;
  if (cabinet) {
    support = cabinet.support;
    if (cabinet.feets) {
      feet = cabinet.getFeet(PositionAngle.FrontRight);
    }
  }

  function onLeave() {
    // Restore the last history element
    dispatch(restoreCabinet(cabinet));
  }

  const allowCustomization =
    (context === FurnitureEditorContext.sales || !isProduction) && !isMobile;

  return (
    <StyledSupportOptions>
      <SectionOptions title={t('Feets')} diviser>
        <BigOptionButton
          imgSrc={noFeets}
          title={t('No feets')}
          handleClick={() => {
            dispatch(setSupportRequestAction(CabinetSupport.NoSupport));
            dispatch(displaySupportPanelAction(false));
          }}
          handleHover={() => {
            dispatch(setSupportRequestAction(CabinetSupport.NoSupport, true));
          }}
          handleDoubleClick={() => {
            dispatch(displaySupportPanelAction(false));
          }}
          onLeave={onLeave}
          selected={support === CabinetSupport.NoSupport}
        />
        <BigOptionButton
          imgSrc={feets10}
          title={t('Classic feets')}
          subtitle="10 cm"
          handleClick={() => {
            dispatch(
              setSupportRequestAction(
                CabinetSupport.Feet,
                false,
                100,
                Material.Wood,
              ),
            );
            setDisplayCustom(false);
            if (!allowCustomization) dispatch(displaySupportPanelAction(false));
          }}
          handleDoubleClick={() => {
            if (!allowCustomization) dispatch(displaySupportPanelAction(false));
          }}
          handleHover={() => {
            dispatch(
              setSupportRequestAction(
                CabinetSupport.Feet,
                true,
                100,
                Material.Wood,
              ),
            );
          }}
          onLeave={onLeave}
          selected={
            feet && feet.height === 100 && feet.base.material === Material.Wood
          }
        />
        <BigOptionButton
          imgSrc={feetsA10}
          title={t('Adjustable feets')}
          subtitle={`11cm ${t('to')} 12.3cm`}
          handleClick={() => {
            dispatch(
              setSupportRequestAction(
                CabinetSupport.AdjustableFeet,
                false,
                100,
                Material.Aluminium,
              ),
            );
            setDisplayCustom(false);
            if (!allowCustomization) dispatch(displaySupportPanelAction(false));
          }}
          handleDoubleClick={() => {
            if (!allowCustomization) dispatch(displaySupportPanelAction(false));
          }}
          handleHover={() => {
            dispatch(
              setSupportRequestAction(
                CabinetSupport.AdjustableFeet,
                true,
                100,
                Material.Aluminium,
              ),
            );
          }}
          onLeave={onLeave}
          selected={
            feet &&
            feet.height === 100 &&
            cabinet.support === CabinetSupport.AdjustableFeet
          }
        />
        {allowCustomization && (
          <FeetsDimensionsForm
            display={displayCustom}
            setDisplayCustom={setDisplayCustom}
            foot={foot}
          />
        )}
      </SectionOptions>

      <SectionOptions title={t('Wheels')} diviser>
        <BigOptionButton
          imgSrc={wheelsWhite}
          title={t('On wheels')}
          subtitle={t('with white plate')}
          handleClick={() => {
            dispatch(setSupportRequestAction(CabinetSupport.WheelWhite));
            dispatch(displaySupportPanelAction(false));
          }}
          handleDoubleClick={() => {
            dispatch(displaySupportPanelAction(false));
          }}
          handleHover={() => {
            dispatch(setSupportRequestAction(CabinetSupport.WheelWhite, true));
          }}
          onLeave={onLeave}
          selected={support === CabinetSupport.WheelWhite}
        />
        <BigOptionButton
          imgSrc={wheelsBlack}
          title={t('On wheels')}
          subtitle={t('with black plate')}
          handleClick={() => {
            dispatch(setSupportRequestAction(CabinetSupport.WheelBlack));
            dispatch(displaySupportPanelAction(false));
          }}
          handleDoubleClick={() => {
            dispatch(displaySupportPanelAction(false));
          }}
          handleHover={() => {
            dispatch(setSupportRequestAction(CabinetSupport.WheelBlack, true));
          }}
          onLeave={onLeave}
          selected={support === CabinetSupport.WheelBlack}
        />
      </SectionOptions>

      <SectionOptions title={t('To hang')}>
        <BigOptionButton
          imgSrc={hang}
          title={t('Suspension')}
          subtitle={t('with hooks')}
          handleClick={() => {
            dispatch(setSupportRequestAction(CabinetSupport.ToHangHook));
            dispatch(displaySupportPanelAction(false));
          }}
          handleDoubleClick={() => {
            dispatch(displaySupportPanelAction(false));
          }}
          handleHover={() => {
            dispatch(setSupportRequestAction(CabinetSupport.ToHangHook, true));
          }}
          onLeave={onLeave}
          selected={support === CabinetSupport.ToHangHook}
        />
        {(context === FurnitureEditorContext.sales || !isProduction) && (
          <BigOptionButton
            imgSrc={hangByCrossRails}
            title={t('Suspension')}
            subtitle={t('with crossrails')}
            handleClick={() => {
              dispatch(setSupportRequestAction(CabinetSupport.ToHangScrew));
              dispatch(displaySupportPanelAction(false));
            }}
            handleDoubleClick={() => {
              dispatch(displaySupportPanelAction(false));
            }}
            handleHover={() => {
              dispatch(
                setSupportRequestAction(CabinetSupport.ToHangScrew, true),
              );
            }}
            onLeave={onLeave}
            selected={support === CabinetSupport.ToHangScrew}
          />
        )}
      </SectionOptions>
    </StyledSupportOptions>
  );
};

export default SupportOptions;

const StyledSupportOptions = styled.div`
  //mx-4 flex justify-center
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  justify-content: left;
  margin-left: 1rem;
  margin-right: 1rem;

  @media (min-width: 1024px) {
    justify-content: center;
  }
`;
